import { useEffect, useCallback } from 'react';

export interface Shortcut {
  key: string;
  description: string;
  action: () => void;
  ctrl?: boolean;
  shift?: boolean;
  global?: boolean; // works even when input is focused
}

export function useKeyboardShortcuts(shortcuts: Shortcut[], enabled: boolean = true) {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!enabled) return;

    // Skip if typing in an input (unless shortcut is global)
    const target = e.target as HTMLElement;
    const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

    for (const shortcut of shortcuts) {
      if (isInput && !shortcut.global) continue;

      const keyMatch = e.key.toLowerCase() === shortcut.key.toLowerCase();
      const ctrlMatch = shortcut.ctrl ? (e.ctrlKey || e.metaKey) : true;
      const shiftMatch = shortcut.shift ? e.shiftKey : true;

      if (keyMatch && ctrlMatch && shiftMatch) {
        e.preventDefault();
        shortcut.action();
        return;
      }
    }
  }, [shortcuts, enabled]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

// Common shortcuts map for documentation.
// Keyboard as consciousness-body in CineVault's dimension: each shortcut extends
// the carrier's operational capacity beyond the cursor alone.
export const GLOBAL_SHORTCUTS = {
  '?': 'Show keyboard shortcuts',
  '/': 'Open search',
  'Escape': 'Close modal',
  'Shift + H': 'Go to Home',
  'Shift + D': 'Go to Discover',
  'Shift + W': 'Go to Watchlist',
};

export const PLAYER_SHORTCUTS = {
  'Space': 'Play/Pause',
  'M': 'Focus player audio controls',
  'F': 'Fullscreen',
  'S': 'Next source',
  'ArrowUp': 'Channel up',
  'ArrowDown': 'Channel down',
  'G': 'Toggle TV Guide',
};
